import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTopButton from "../components/BackToTopButton";
import FloatingSocialButtons from "../components/FloatingSocialButtons";
import { Helmet } from "react-helmet";
import { PROJECT_CATEGORIES } from '@/constants/projectCategories';

interface Project {
  id: string;
  title: string;
  category: string;
  image?: string;
  link?: string;
  description?: string;
  created_at: string;
  project_type?: 'link' | 'video';
  video_url?: string;
  thumbnail_url?: string;
  type?: 'facebook' | 'youtube';
}

const ProjectsPage = () => {
  const [selectedFilter, setSelectedFilter] = useState<string>('All');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = "Project Portfolio - DG IT Pvt. Ltd.";
    fetchProjects();
    
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsModalVisible(false);
      }
    };
    
    if (isModalVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModalVisible]);

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Process projects to add type field
      const processedProjects = (data || []).map(project => ({
        ...project,
        type: project.project_type === 'video' 
          ? 'youtube' as const 
          : project.link?.includes('youtube') || project.link?.includes('youtu.be') 
            ? 'youtube' as const 
            : 'facebook' as const
      }));
      
      setProjects(processedProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get standardized categories
  const allCategories = ['All', ...PROJECT_CATEGORIES];
  
  const filteredProjects = selectedFilter === 'All' 
    ? projects 
    : projects.filter(project => project.category === selectedFilter);

  const openProjectModal = (project: Project) => {
    setSelectedProject(project);
    setIsModalVisible(true);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Projects Portfolio - DG IT Pvt. Ltd.</title>
        <meta name="description" content="Explore our portfolio of successful projects including web development, video ads, logo design and SEO optimization." />
        <meta name="keywords" content="portfolio, projects, web design, logo design, video ads, SEO, digital marketing" />
        <meta property="og:title" content="Projects Portfolio - DG IT Pvt. Ltd." />
        <meta property="og:description" content="Explore our portfolio of successful digital projects." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <Navbar />
      <main className="flex-grow">
        <div className="bg-gradient-to-b from-blue-50 to-white py-20 px-4 md:px-6">
          <div className="container mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-center mb-4">Our <span className="text-blue-600">Projects</span></h1>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-12">
              Explore our portfolio of successful projects that showcase our creativity, technical expertise, and commitment to delivering exceptional results.
            </p>
            
            {/* Filter Categories - Scrollable on mobile */}
            <div className="flex overflow-x-auto pb-4 mb-8 md:mb-12 md:justify-center md:overflow-visible">
              <div className="flex space-x-2 md:flex-wrap md:justify-center md:gap-3">
                {allCategories.map((category, index) => (
                  <motion.button
                    key={index}
                    onClick={() => setSelectedFilter(category)}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                      selectedFilter === category 
                        ? 'bg-blue-500 text-white shadow-md' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {category}
                  </motion.button>
                ))}
              </div>
            </div>
            
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                {/* Projects Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {filteredProjects.map((project, index) => (
                    <motion.div 
                      key={project.id} 
                      className="bg-white rounded-xl shadow-md overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:scale-[1.02] cursor-pointer"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      onClick={() => openProjectModal(project)}
                    >
                      <div className="relative h-48 md:h-56 overflow-hidden group">
                        <img 
                          src={project.project_type === 'video' ? project.thumbnail_url : project.image} 
                          alt={project.title} 
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                          style={{ imageRendering: 'crisp-edges' }}
                        />
                        {project.project_type === 'video' && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity">
                              <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="absolute inset-x-0 bottom-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                          <span className="inline-block px-3 py-1 bg-blue-500 text-white text-xs font-medium rounded-full mb-2">
                            {project.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-5">
                        <h3 className="text-xl font-bold mb-2 hover:text-blue-600 transition-colors">{project.title}</h3>
                        <p className="text-gray-600 mb-4 line-clamp-2">{project.description}</p>
                        <div className="flex justify-between items-center">
                          <button 
                            onClick={() => openProjectModal(project)}
                            className="text-blue-500 font-medium hover:text-blue-700 transition-colors flex items-center group"
                          >
                            View Details
                            <svg
                              className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M14 5l7 7m0 0l-7 7m7-7H3"
                              ></path>
                            </svg>
                          </button>
                          {(project.link || project.video_url) && (
                            <a 
                              href={project.project_type === 'video' ? project.video_url : project.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              {project.project_type === 'video' || project.type === 'youtube' ? (
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                </svg>
                              ) : (
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                              )}
                            </a>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Project Modal */}
                <AnimatePresence>
                  {isModalVisible && selectedProject && (
                    <motion.div
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={(e) => {
                        if (e.target === e.currentTarget) {
                          setIsModalVisible(false);
                        }
                      }}
                    >
                      <motion.div
                        ref={modalRef}
                        className="bg-white rounded-xl overflow-hidden max-w-4xl w-full max-h-[90vh] relative"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                      >
                        <button
                          className="absolute top-4 right-4 z-10 bg-white/80 backdrop-blur-sm p-2 rounded-full text-gray-700 hover:text-gray-900 hover:bg-white transition-colors"
                          onClick={() => setIsModalVisible(false)}
                          aria-label="Close modal"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        
                        <div className="p-6 text-center">
                          {selectedProject.project_type === 'video' ? (
                            <div className="w-full max-h-[50vh] mb-4 rounded-lg overflow-hidden">
                              <iframe
                                width="100%"
                                height="400"
                                src={selectedProject.video_url?.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                                title={selectedProject.title}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="rounded-lg"
                              ></iframe>
                            </div>
                          ) : (
                            <img
                              src={selectedProject.image}
                              alt={selectedProject.title}
                              className="w-full max-h-[50vh] object-contain mb-4 rounded-lg"
                            />
                          )}
                          <h3 className="text-xl font-bold mb-2">{selectedProject.title}</h3>
                          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full mb-4">
                            {selectedProject.category}
                          </span>
                          {selectedProject.description && (
                            <p className="text-gray-600 mb-4">{selectedProject.description}</p>
                          )}
                          {(selectedProject.link || selectedProject.video_url) && (
                            <a
                              href={selectedProject.project_type === 'video' ? selectedProject.video_url : selectedProject.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                            >
                              {selectedProject.project_type === 'video' ? 'Watch on YouTube' : 'Visit Project'}
                            </a>
                          )}
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
      <BackToTopButton />
      <FloatingSocialButtons />
    </div>
  );
};

export default ProjectsPage; 